import { Controller } from "@hotwired/stimulus"

/**
 * Autosubmit Controller
 *
 * Automatically submits a form after user input with configurable debounce delay.
 * Prevents excessive form submissions by waiting for user to stop typing.
 *
 * Usage:
 *   <form data-controller="autosubmit"
 *         data-autosubmit-delay-value="300">
 *     <input data-action="input->autosubmit#submit">
 *   </form>
 *
 * Values:
 * - delay: Debounce delay in milliseconds (default: 300)
 *
 * Actions:
 * - submit(): Trigger debounced form submission
 *
 * Features:
 * - Debounce: Waits for user to stop typing before submitting
 * - Cleanup: Automatically clears pending timeouts on disconnect
 * - Configurable delay: Adjust debounce timing per form
 */
export default class extends Controller {
  // Stimulus values
  static values = {
    delay: { type: Number, default: 300 }
  }

  /**
   * Initialize controller
   * Called when controller connects to DOM
   */
  connect() {
    // Timeout ID for debounce cleanup
    this.timeout = null
  }

  /**
   * Cleanup on disconnect
   * Clears any pending timeouts to prevent memory leaks
   */
  disconnect() {
    this.clearTimeout()
  }

  /**
   * Submit form with debounce
   * Waits for delayValue milliseconds after last call before submitting
   *
   * Example:
   *   User types "recipe" quickly
   *   - Each keystroke calls submit()
   *   - Previous timeout is cancelled
   *   - Only the final timeout (after user stops typing) triggers submission
   */
  submit() {
    // Clear any existing timeout
    this.clearTimeout()

    // Set new timeout for form submission
    this.timeout = setTimeout(() => {
      // Use requestSubmit() instead of submit() to:
      // 1. Trigger submit event (for event listeners)
      // 2. Run form validation
      // 3. Properly integrate with Turbo
      this.element.requestSubmit()
    }, this.delayValue)
  }

  /**
   * Clear pending timeout
   * Private helper method
   */
  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
  }
}